import React from "react";
import type { BidAttempt } from "../../routes/pages/negotiate.bidAttempt";
import "../Button/Button.css";
import "./PendingBidRecoveryPanel.css";

const currency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export type PendingBidRecoveryViewProps = {
  attempts: BidAttempt[];
  storageError: boolean;
  canConsult: boolean;
  sending: Record<string, boolean>;
  notice?: string;
  onConsult: (attempt: BidAttempt) => void;
  onDismiss: () => void;
};

/**
 * Pure presentation; it never reads credentials or sends a bid.
 * @story frame 520x360
 * @story args { attempts: [{ key: "12345678-1234-4123-8123-123456789abc", orgId: "org-a", collaboratorId: "collaborator-a", payload: { postId: "retired-post", lastBidAmount: 125, lastBidDiff: 25 } }], storageError: false, canConsult: true, sending: {}, notice: undefined }
 * @story state ReadOnly { canConsult: false }
 * @story state StorageUnavailable { attempts: [], storageError: true }
 * @story state Consulting { sending: { "12345678-1234-4123-8123-123456789abc": true } }
 * @story state Uncertain { notice: "O resultado do lance enviado ainda não foi confirmado. Consulte a mesma tentativa." }
 */
export function PendingBidRecoveryView({
  attempts,
  storageError,
  canConsult,
  sending,
  notice,
  onConsult,
  onDismiss,
}: PendingBidRecoveryViewProps) {
  return (
    <section
      className="pendingBidRecovery"
      aria-label="Lances pendentes"
      data-testid={attempts.length || storageError ? "pending-bid-recovery-panel" : undefined}
    >
      <h2>
        {attempts.length || storageError
          ? "Lances pendentes de confirmação"
          : "Resultado da consulta"}
      </h2>
      {storageError ? (
        <p role="alert">
          Não foi possível ler as tentativas salvas. Seus registros foram preservados.
        </p>
      ) : (
        <>
          {!!attempts.length && (
            <p>Confira o resultado do lance já enviado antes de fazer outro nessa publicação.</p>
          )}
          {attempts.map((attempt) => (
            <div className="pendingBidRecoveryAttempt" key={attempt.key}>
              <span>Lance de {currency.format(attempt.payload.lastBidAmount)}</span>
              {canConsult && (
                <button
                  type="button"
                  className="btnContainer btnBrand"
                  data-testid={`pending-bid-query-${attempt.payload.postId}`}
                  disabled={sending[attempt.key]}
                  onClick={() => onConsult(attempt)}
                >
                  {sending[attempt.key] ? "Consultando..." : "Consultar lance enviado"}
                </button>
              )}
            </div>
          ))}
          {!!attempts.length && !canConsult && (
            <p>
              Peça a um responsável para restabelecer sua permissão de lances antes de consultar.
            </p>
          )}
        </>
      )}
      {notice && <p role="alert">{notice}</p>}
      {!attempts.length && !storageError && notice && (
        <button type="button" className="btnContainer" onClick={onDismiss}>
          Fechar
        </button>
      )}
    </section>
  );
}
